// Iter 83.2 / Phase E4 Sprint 2 — Assets Console.
// Master + Assignment dialog + Return dialog + Detail+Audit.
// Implements D17 (categories) + D18 (return recommendations).
// Feeds Sprint 3 FNF via clearance-status endpoint.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Plus, PencilSimple, MagnifyingGlass, Briefcase, UserPlus,
    ArrowUDownLeft, ListBullets, TrashSimple, Warning, Upload,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";

const STATUS_TONE = {
    Procured: "bg-zinc-100 text-zinc-700",
    InStock: "bg-emerald-100 text-emerald-800",
    Assigned: "bg-amber-100 text-amber-800",
    InUse: "bg-sky-100 text-sky-800",
    Damaged: "bg-rose-100 text-rose-700",
    Lost: "bg-rose-200 text-rose-900",
    WrittenOff: "bg-slate-200 text-slate-600",
};

const OUTCOME_TONE = {
    Good: "bg-emerald-100 text-emerald-800",
    Damaged: "bg-rose-100 text-rose-700",
    Lost: "bg-rose-200 text-rose-900",
    NotReturned: "bg-rose-300 text-rose-950",
};

const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const EMPTY_ASSET = {
    asset_tag: "", category_id: "", model: "", serial_number: "",
    purchase_date: "", purchase_amount: "", depreciated_value: "",
    description: "", location: "",
};

export default function AssetsConsole() {
    const { can } = useAuth();
    const canEdit = can("assets:admin");
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    // New/edit asset
    const [assetOpen, setAssetOpen] = useState(false);
    const [editingAsset, setEditingAsset] = useState(null);
    const [assetForm, setAssetForm] = useState(EMPTY_ASSET);
    // Assign dialog
    const [assignOpen, setAssignOpen] = useState(false);
    const [assignAsset, setAssignAsset] = useState(null);
    const [assignForm, setAssignForm] = useState({ employee_id: "", location: "", notes: "" });
    // Return dialog
    const [returnOpen, setReturnOpen] = useState(false);
    const [returnAsset, setReturnAsset] = useState(null);
    const [returnAssignment, setReturnAssignment] = useState(null);
    const [returnForm, setReturnForm] = useState({ outcome: "Good", operator_recommended_amount: "", reason: "" });
    // Iter 83.5 / Sprint 5 — Bulk Asset Import
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkCsv, setBulkCsv] = useState("");
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkLoading, setBulkLoading] = useState(false);
    // Detail dialog
    const [detail, setDetail] = useState(null);
    const [audit, setAudit] = useState([]);
    const [assignmentHistory, setAssignmentHistory] = useState([]);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (categoryFilter) params.category_id = categoryFilter;
            const [list, cats, m, emp] = await Promise.all([
                api.get("/assets/list", { params }),
                api.get("/assets/category/list", { params: { is_active: true } }),
                api.get("/assets/meta"),
                api.get("/hr/employee/list", { params: { limit: 500 } }).catch(() => ({ data: { items: [] } })),
            ]);
            setItems(list.data.items);
            setCategories(cats.data.items);
            setMeta(m.data);
            setEmployees(emp.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load assets"));
        } finally {
            setLoading(false);
        }
    }, [statusFilter, categoryFilter]);
    useEffect(() => { reload(); }, [reload]);

    const filtered = useMemo(() => {
        if (!search) return items;
        const q = search.toLowerCase();
        return items.filter((it) =>
            (it.asset_tag || "").toLowerCase().includes(q)
            || (it.model || "").toLowerCase().includes(q)
            || (it.serial_number || "").toLowerCase().includes(q)
            || (it.current_employee_name || "").toLowerCase().includes(q)
        );
    }, [items, search]);

    const openNewAsset = () => { setEditingAsset(null); setAssetForm(EMPTY_ASSET); setAssetOpen(true); };
    const openEditAsset = (it) => {
        setEditingAsset(it);
        setAssetForm({
            asset_tag: it.asset_tag, category_id: it.category_id, model: it.model || "",
            serial_number: it.serial_number || "",
            purchase_date: it.purchase_date || "",
            purchase_amount: it.purchase_amount ?? "",
            depreciated_value: it.depreciated_value ?? "",
            description: it.description || "", location: it.location || "",
        });
        setAssetOpen(true);
    };

    const saveAsset = async () => {
        if (!assetForm.asset_tag?.trim() || !assetForm.category_id) {
            toast.error("Tag and category are required");
            return;
        }
        const payload = {
            ...assetForm,
            purchase_amount: assetForm.purchase_amount === "" ? null : Number(assetForm.purchase_amount),
            depreciated_value: assetForm.depreciated_value === "" ? null : Number(assetForm.depreciated_value),
        };
        if (!payload.purchase_date) delete payload.purchase_date;
        try {
            if (editingAsset) {
                const { asset_tag, ...patch } = payload;
                await api.patch(`/assets/${editingAsset.id}`, patch);
                toast.success(`Updated ${editingAsset.asset_tag}`);
            } else {
                await api.post("/assets", payload);
                toast.success(`Created ${payload.asset_tag}`);
            }
            setAssetOpen(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };

    const removeAsset = async (it) => {
        if (!window.confirm(`Delete asset ${it.asset_tag}? Only InStock assets are deletable.`)) return;
        try {
            await api.delete(`/assets/${it.id}`);
            toast.success("Deleted");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    const openAssign = (it) => {
        setAssignAsset(it);
        setAssignForm({ employee_id: "", location: it.location || "", notes: "" });
        setAssignOpen(true);
    };

    const submitAssign = async () => {
        if (!assignAsset || !assignForm.employee_id) { toast.error("Employee required"); return; }
        try {
            await api.post(`/assets/${assignAsset.id}/assign`, assignForm);
            toast.success(`Assigned ${assignAsset.asset_tag}`);
            setAssignOpen(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Assign failed"));
        }
    };

    const openReturn = async (it) => {
        if (!it.current_assignment_id) { toast.error("Asset has no active assignment"); return; }
        setReturnAsset(it);
        setReturnForm({ outcome: "Good", operator_recommended_amount: "", reason: "" });
        try {
            const { data } = await api.get(`/assets/assignment/${it.current_assignment_id}`);
            setReturnAssignment(data);
            setReturnOpen(true);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load assignment"));
        }
    };

    const submitReturn = async () => {
        if (!returnAsset || !returnAssignment) return;
        const payload = {
            outcome: returnForm.outcome,
            reason: returnForm.reason,
        };
        if (returnForm.outcome === "Damaged") {
            if (returnForm.operator_recommended_amount === "" || returnForm.operator_recommended_amount === null) {
                toast.error("Damaged returns need a recommended recovery amount");
                return;
            }
            if (!returnForm.reason?.trim()) {
                toast.error("Damaged returns need a reason");
                return;
            }
            payload.operator_recommended_amount = Number(returnForm.operator_recommended_amount);
        }
        try {
            await api.post(`/assets/assignment/${returnAssignment.id}/return`, payload);
            toast.success(`Returned ${returnAsset.asset_tag}`);
            setReturnOpen(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Return failed"));
        }
    };

    // Iter 83.5 / Sprint 5 — Bulk Asset Import handler
    const runBulk = async (dryRun) => {
        setBulkLoading(true);
        setBulkResult(null);
        try {
            const lines = bulkCsv.trim().split(/\r?\n/).filter(Boolean);
            if (lines.length < 2) {
                toast.error("CSV needs a header line + at least one data row");
                setBulkLoading(false);
                return;
            }
            const headers = lines[0].split(",").map((h) => h.trim());
            const rows = lines.slice(1).map((ln) => {
                const cells = ln.split(",").map((c) => c.trim());
                const row = {};
                headers.forEach((h, i) => { row[h] = cells[i] || ""; });
                // Numeric coercion
                if (row.purchase_amount) row.purchase_amount = Number(row.purchase_amount);
                if (row.depreciated_value) row.depreciated_value = Number(row.depreciated_value);
                return row;
            });
            const { data } = await api.post(`/assets/bulk-import`, {
                rows,
                dry_run: dryRun,
            });
            setBulkResult(data);
            if (!dryRun) {
                toast.success(`Imported ${data.summary.inserted || 0} asset(s).`);
                reload();
            } else {
                toast.success(`Dry run: ${data.summary.ok} valid · ${data.summary.duplicate || 0} duplicate · ${data.summary.invalid_category || 0} bad category · ${data.summary.invalid_date || 0} bad date`);
            }
        } catch (err) {
            toast.error(formatApiError(err, "Bulk import failed"));
        } finally {
            setBulkLoading(false);
        }
    };

    const openDetail = async (it) => {
        setDetail(it);
        setAudit([]);
        setAssignmentHistory([]);
        try {
            const [a, h] = await Promise.all([
                api.get(`/assets/${it.id}/audit`),
                // Use employee/list when current_employee_id exists; otherwise leave empty.
                Promise.resolve({ data: { items: [] } }),
            ]);
            setAudit(a.data.items);
            // Pull assignment history from audit rows that have assignment_id
            const asnIds = [...new Set(a.data.items.filter((r) => r.assignment_id).map((r) => r.assignment_id))];
            const asnDocs = await Promise.all(asnIds.map((id) =>
                api.get(`/assets/assignment/${id}`).then((r) => r.data).catch(() => null)
            ));
            setAssignmentHistory(asnDocs.filter(Boolean));
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load detail"));
        }
    };

    const writeoff = async (it) => {
        const reason = window.prompt(`Write off ${it.asset_tag}? Reason:`);
        if (!reason) return;
        try {
            await api.post(`/assets/${it.id}/writeoff`, { reason });
            toast.success("Written off");
            setDetail(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Write-off failed"));
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1600px]" data-testid="assets-console-page">
            <PageHeader
                eyebrow="Asset Management"
                title="Assets Console"
                description={`${items.length} asset${items.length === 1 ? "" : "s"} on file · D17 12 categories seeded · D18 recovery-recommendation engine`}
                actions={canEdit ? (
                    <div className="flex gap-2">
                        <Button onClick={() => setBulkOpen(true)} size="sm" variant="outline" data-testid="asset-bulk-import-btn">
                            <Upload size={14} className="mr-1" /> Bulk Import
                        </Button>
                        <Button onClick={openNewAsset} size="sm" data-testid="asset-new-btn">
                            <Plus size={14} className="mr-1" /> New Asset
                        </Button>
                    </div>
                ) : null}
            />
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex gap-3 flex-wrap items-end">
                    <div className="flex-1 min-w-[240px]">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Search</Label>
                        <div className="relative">
                            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input className="pl-9" placeholder="Tag, model, serial, or assignee…"
                                value={search} onChange={(e) => setSearch(e.target.value)}
                                data-testid="assets-search" />
                        </div>
                    </div>
                    <div className="w-48">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Status</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2"
                            value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                            data-testid="assets-status-filter">
                            <option value="">All</option>
                            {(meta?.asset_states || []).map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div className="w-56">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Category</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2"
                            value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                            data-testid="assets-cat-filter">
                            <option value="">All</option>
                            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                </div>
            </section>
            <section className="mt-4 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="assets-table">
                {loading ? <div className="text-center py-12 text-sm text-slate-400">Loading…</div> : (
                    <table className="w-full text-sm min-w-[1300px]">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Tag</th>
                                <th className="text-left px-3 py-2 font-medium">Category</th>
                                <th className="text-left px-3 py-2 font-medium">Model</th>
                                <th className="text-left px-3 py-2 font-medium">Serial</th>
                                <th className="text-right px-3 py-2 font-medium">Dep. Value</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                                <th className="text-left px-3 py-2 font-medium">Assigned To</th>
                                <th className="text-left px-3 py-2 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((it) => (
                                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`asset-row-${it.asset_tag}`}>
                                    <td className="px-3 py-2 font-mono font-semibold">{it.asset_tag}</td>
                                    <td className="px-3 py-2 text-xs">{it.category_name_snapshot}</td>
                                    <td className="px-3 py-2 text-xs">{it.model || "—"}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{it.serial_number || "—"}</td>
                                    <td className="px-3 py-2 text-right font-mono text-xs">{it.depreciated_value ? fmtMoney(it.depreciated_value) : "—"}</td>
                                    <td className="px-3 py-2"><Badge className={`text-[10px] ${STATUS_TONE[it.status] || ""}`}>{it.status}</Badge></td>
                                    <td className="px-3 py-2 text-xs">{it.current_employee_name || <span className="text-slate-400">—</span>}</td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-1">
                                            <Button variant="ghost" size="sm" onClick={() => openDetail(it)} data-testid={`asset-view-${it.asset_tag}`}>
                                                <ListBullets size={14} />
                                            </Button>
                                            {canEdit && it.status === "InStock" && (
                                                <Button variant="ghost" size="sm" onClick={() => openAssign(it)} data-testid={`asset-assign-${it.asset_tag}`}>
                                                    <UserPlus size={14} />
                                                </Button>
                                            )}
                                            {canEdit && (it.status === "Assigned" || it.status === "InUse") && (
                                                <Button variant="ghost" size="sm" onClick={() => openReturn(it)} data-testid={`asset-return-${it.asset_tag}`}>
                                                    <ArrowUDownLeft size={14} />
                                                </Button>
                                            )}
                                            {canEdit && (
                                                <Button variant="ghost" size="sm" onClick={() => openEditAsset(it)}>
                                                    <PencilSimple size={12} />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr><td colSpan={8} className="text-center py-10 text-slate-400 italic text-sm">No assets match.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </section>

            {/* New / Edit asset dialog */}
            <Dialog open={assetOpen} onOpenChange={setAssetOpen}>
                <DialogContent className="max-w-2xl" data-testid="asset-form-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Briefcase size={18} /> {editingAsset ? `Edit ${editingAsset.asset_tag}` : "New Asset"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                            <Label>Tag *</Label>
                            <Input value={assetForm.asset_tag} disabled={!!editingAsset}
                                onChange={(e) => setAssetForm({ ...assetForm, asset_tag: e.target.value })}
                                data-testid="asset-form-tag" />
                        </div>
                        <div>
                            <Label>Category *</Label>
                            <select className="w-full border rounded-md text-sm h-9 px-2 mt-1"
                                value={assetForm.category_id}
                                onChange={(e) => setAssetForm({ ...assetForm, category_id: e.target.value })}
                                data-testid="asset-form-cat">
                                <option value="">— Select —</option>
                                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Model</Label>
                            <Input value={assetForm.model}
                                onChange={(e) => setAssetForm({ ...assetForm, model: e.target.value })} />
                        </div>
                        <div>
                            <Label>Serial #</Label>
                            <Input value={assetForm.serial_number}
                                onChange={(e) => setAssetForm({ ...assetForm, serial_number: e.target.value })} />
                        </div>
                        <div>
                            <Label>Purchase Date</Label>
                            <Input type="date" value={assetForm.purchase_date}
                                onChange={(e) => setAssetForm({ ...assetForm, purchase_date: e.target.value })} />
                        </div>
                        <div>
                            <Label>Purchase Amount (₹)</Label>
                            <Input type="number" min="0" value={assetForm.purchase_amount}
                                onChange={(e) => setAssetForm({ ...assetForm, purchase_amount: e.target.value })} />
                        </div>
                        <div>
                            <Label>Depreciated Value (₹)</Label>
                            <Input type="number" min="0" value={assetForm.depreciated_value}
                                onChange={(e) => setAssetForm({ ...assetForm, depreciated_value: e.target.value })}
                                data-testid="asset-form-dep" />
                        </div>
                        <div>
                            <Label>Location</Label>
                            <Input value={assetForm.location}
                                onChange={(e) => setAssetForm({ ...assetForm, location: e.target.value })} />
                        </div>
                        <div className="col-span-2">
                            <Label>Description</Label>
                            <Input value={assetForm.description}
                                onChange={(e) => setAssetForm({ ...assetForm, description: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setAssetOpen(false)}>Cancel</Button>
                        <Button onClick={saveAsset} data-testid="asset-form-save">{editingAsset ? "Save" : "Create"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Assign dialog */}
            <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
                <DialogContent className="max-w-md" data-testid="asset-assign-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><UserPlus size={18} /> Assign {assignAsset?.asset_tag}</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-3 text-sm">
                        <div>
                            <Label>Employee *</Label>
                            <select className="w-full border rounded-md text-sm h-9 px-2 mt-1"
                                value={assignForm.employee_id}
                                onChange={(e) => setAssignForm({ ...assignForm, employee_id: e.target.value })}
                                data-testid="asset-assign-emp">
                                <option value="">— Select —</option>
                                {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} ({e.id})</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Location / Site</Label>
                            <Input value={assignForm.location}
                                onChange={(e) => setAssignForm({ ...assignForm, location: e.target.value })} />
                        </div>
                        <div>
                            <Label>Notes</Label>
                            <Input value={assignForm.notes}
                                onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setAssignOpen(false)}>Cancel</Button>
                        <Button onClick={submitAssign} data-testid="asset-assign-submit">Assign</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Return dialog */}
            <Dialog open={returnOpen} onOpenChange={setReturnOpen}>
                <DialogContent className="max-w-md" data-testid="asset-return-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><ArrowUDownLeft size={18} /> Return {returnAsset?.asset_tag}</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-3 text-sm">
                        <div>
                            <Label>Outcome *</Label>
                            <select className="w-full border rounded-md text-sm h-9 px-2 mt-1"
                                value={returnForm.outcome}
                                onChange={(e) => setReturnForm({ ...returnForm, outcome: e.target.value })}
                                data-testid="asset-return-outcome">
                                <option value="Good">Good — no recovery</option>
                                <option value="Damaged">Damaged — operator recommends recovery</option>
                                <option value="Lost">Lost — full recoverable amount</option>
                            </select>
                        </div>
                        {returnForm.outcome === "Damaged" && (
                            <>
                                <div>
                                    <Label>Recommended Recovery (₹) *</Label>
                                    <Input type="number" min="0" value={returnForm.operator_recommended_amount}
                                        onChange={(e) => setReturnForm({ ...returnForm, operator_recommended_amount: e.target.value })}
                                        placeholder={`Max ${fmtMoney(returnAssignment?.asset_depreciated_value_snapshot || 0)}`}
                                        data-testid="asset-return-amount" />
                                    <p className="text-[10px] text-slate-500 mt-1">Recommendation only — capped at depreciated value. Actual deduction happens at Exit/FNF (Sprint 3) with operator override + audit.</p>
                                </div>
                                <div>
                                    <Label>Reason *</Label>
                                    <Input value={returnForm.reason}
                                        onChange={(e) => setReturnForm({ ...returnForm, reason: e.target.value })}
                                        placeholder="e.g. Screen cracked"
                                        data-testid="asset-return-reason" />
                                </div>
                            </>
                        )}
                        {returnForm.outcome === "Lost" && (
                            <>
                                <div>
                                    <Label>Reason / Context</Label>
                                    <Input value={returnForm.reason}
                                        onChange={(e) => setReturnForm({ ...returnForm, reason: e.target.value })}
                                        placeholder="e.g. Misplaced during travel" />
                                </div>
                                <div className="text-xs p-2 bg-rose-50 border border-rose-200 rounded">
                                    <Warning size={14} className="inline mr-1 text-rose-600" />
                                    Full depreciated value <span className="font-mono">{fmtMoney(returnAssignment?.asset_depreciated_value_snapshot || 0)}</span> will be recommended for recovery at FNF.
                                </div>
                            </>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setReturnOpen(false)}>Cancel</Button>
                        <Button onClick={submitReturn} data-testid="asset-return-submit">Submit Return</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail dialog */}
            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="asset-detail-dialog">
                    {detail && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <Briefcase size={18} /> {detail.asset_tag}
                                    <Badge className={`text-[10px] ${STATUS_TONE[detail.status] || ""} ml-2`}>{detail.status}</Badge>
                                </DialogTitle>
                            </DialogHeader>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Category</div>
                                    <div>{detail.category_name_snapshot}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Model · Serial</div>
                                    <div className="text-xs font-mono">{detail.model || "—"} · {detail.serial_number || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Purchase · Depreciated</div>
                                    <div className="font-mono">{detail.purchase_amount ? fmtMoney(detail.purchase_amount) : "—"} · {detail.depreciated_value ? fmtMoney(detail.depreciated_value) : "—"}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Currently With</div>
                                    <div>{detail.current_employee_name || <span className="text-slate-400 italic">—</span>}</div>
                                </div>
                                {detail.description && (
                                    <div className="col-span-2">
                                        <div className="text-[10px] uppercase text-slate-500 font-semibold">Description</div>
                                        <div className="text-xs italic">{detail.description}</div>
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-2 mt-3 flex-wrap">
                                {canEdit && detail.status === "InStock" && (
                                    <Button size="sm" onClick={() => { setDetail(null); openAssign(detail); }}>
                                        <UserPlus size={14} className="mr-1" /> Assign
                                    </Button>
                                )}
                                {canEdit && (detail.status === "Assigned" || detail.status === "InUse") && (
                                    <Button size="sm" onClick={() => { setDetail(null); openReturn(detail); }}>
                                        <ArrowUDownLeft size={14} className="mr-1" /> Record Return
                                    </Button>
                                )}
                                {canEdit && (detail.status === "Damaged" || detail.status === "Lost" || detail.status === "InStock") && (
                                    <Button size="sm" variant="outline" onClick={() => writeoff(detail)}>
                                        <TrashSimple size={14} className="mr-1" /> Write Off
                                    </Button>
                                )}
                            </div>

                            {/* Assignment history */}
                            <div className="mt-4">
                                <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Assignment History</div>
                                <div className="overflow-x-auto border border-slate-200 rounded">
                                    <table className="w-full text-xs">
                                        <thead className="bg-slate-50 text-slate-600">
                                            <tr>
                                                <th className="text-left px-2 py-1">Assigned</th>
                                                <th className="text-left px-2 py-1">Returned</th>
                                                <th className="text-left px-2 py-1">Employee</th>
                                                <th className="text-left px-2 py-1">Outcome</th>
                                                <th className="text-right px-2 py-1">Recommended Recovery</th>
                                                <th className="text-left px-2 py-1">Reason</th>
                                                <th className="text-right px-2 py-1">Docs</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {assignmentHistory.map((a) => (
                                                <tr key={a.id} className="border-t border-slate-100">
                                                    <td className="px-2 py-1 text-slate-500">{a.assigned_on}</td>
                                                    <td className="px-2 py-1 text-slate-500">{(a.returned_on || a.returned_at || "").slice(0, 10) || "—"}</td>
                                                    <td className="px-2 py-1">{a.employee_name_snapshot}</td>
                                                    <td className="px-2 py-1">
                                                        {a.return_outcome ? <Badge className={`text-[10px] ${OUTCOME_TONE[a.return_outcome] || ""}`}>{a.return_outcome}</Badge> : <span className="text-slate-400">Active</span>}
                                                    </td>
                                                    <td className="px-2 py-1 text-right font-mono">{a.recommended_recovery ? fmtMoney(a.recommended_recovery) : "—"}</td>
                                                    <td className="px-2 py-1 italic text-slate-600 max-w-xs truncate">{a.return_reason || "—"}</td>
                                                    <td className="px-2 py-1 text-right">
                                                        <a href={`/assets/assignment/${a.id}/handover/print`} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline text-[10px] mr-2" data-testid={`asset-handover-print-${a.id}`}>Handover</a>
                                                        {a.return_outcome && (
                                                            <a href={`/assets/assignment/${a.id}/return/print`} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline text-[10px]" data-testid={`asset-return-print-${a.id}`}>Return</a>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {assignmentHistory.length === 0 && (
                                                <tr><td colSpan={7} className="px-2 py-3 text-center text-slate-400 italic">No assignment history.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="mt-4">
                                <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Audit Trail</div>
                                <ul className="text-xs space-y-1">
                                    {audit.map((a) => (
                                        <li key={a.id} className="font-mono">
                                            <span className="text-slate-500">{(a.created_at || "").slice(0, 19).replace("T", " ")}</span>
                                            {" · "}
                                            <span className="font-semibold">{a.action}</span>
                                            {a.from_state && <span> ({a.from_state} → {a.to_state})</span>}
                                            <span className="text-slate-500"> · {a.actor_email}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Iter 83.5 / Sprint 5 — Bulk Asset Import dialog */}
            <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) { setBulkOpen(false); setBulkResult(null); setBulkCsv(""); } }}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>Bulk Asset Import</DialogTitle>
                        <DialogDescription className="text-xs">
                            Two-step import: dry-run first, then commit. Operator-only (assets:admin).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="text-xs text-slate-600">
                            Paste CSV rows below. Header line required. Columns:
                            <code className="bg-slate-100 px-1 ml-1">asset_tag,category_name,model,serial_number,purchase_date,purchase_amount,depreciated_value,description,location</code>.
                            Use <code>category_name</code> exactly as listed in Asset Categories.
                        </div>
                        <textarea
                            className="w-full border rounded p-2 text-xs font-mono h-40"
                            value={bulkCsv}
                            onChange={(e) => setBulkCsv(e.target.value)}
                            placeholder="asset_tag,category_name,model,serial_number,purchase_date,purchase_amount,depreciated_value,description,location&#10;TST-001,Laptop,ThinkPad X1,SN12345,2026-01-15,90000,80000,Engineering laptop,BLR-HQ"
                            data-testid="bulk-csv-input"
                        />
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" disabled={bulkLoading || !bulkCsv.trim()}
                                onClick={() => runBulk(true)} data-testid="bulk-dry-run-btn">
                                Dry Run (Validate)
                            </Button>
                            <Button size="sm" disabled={bulkLoading || !bulkResult || (bulkResult.summary.ok || 0) === 0}
                                onClick={() => runBulk(false)} data-testid="bulk-commit-btn">
                                Commit {bulkResult ? `${bulkResult.summary.ok} valid row${bulkResult.summary.ok === 1 ? "" : "s"}` : ""}
                            </Button>
                        </div>
                        {bulkResult && (
                            <div className="space-y-2">
                                <div className="grid grid-cols-5 gap-1 text-xs">
                                    {Object.entries(bulkResult.summary).map(([k, v]) => (
                                        <div key={k} className="border rounded p-1 text-center">
                                            <div className="text-[9px] uppercase text-slate-500">{k}</div>
                                            <div className="font-bold">{String(v)}</div>
                                        </div>
                                    ))}
                                </div>
                                <table className="w-full text-[11px] border" data-testid="bulk-results-table">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <th className="text-left px-2 py-1">#</th>
                                            <th className="text-left px-2 py-1">Tag</th>
                                            <th className="text-left px-2 py-1">Outcome</th>
                                            <th className="text-left px-2 py-1">Resolved Category</th>
                                            <th className="text-left px-2 py-1">Messages</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bulkResult.results.map((r) => (
                                            <tr key={r.row} className="border-b">
                                                <td className="px-2 py-0.5">{r.row}</td>
                                                <td className="px-2 py-0.5 font-mono">{r.asset_tag}</td>
                                                <td className={`px-2 py-0.5 ${r.outcome === "ok" ? "text-emerald-700" : "text-rose-700"}`}>{r.outcome}</td>
                                                <td className="px-2 py-0.5 text-slate-600">{r.category_name_resolved || "—"}</td>
                                                <td className="px-2 py-0.5 text-slate-500">{(r.messages || []).join("; ")}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
